import { theme, useResponsive } from "@product/scmp-sdk";
import { useMemo } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ArticleImageGallery } from "~/components/article/article-image-gallery";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import {
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "~/components/article/article-render/hooks";
import {
  RightAvailableSpaceTracker,
  WidgetsBottomAvailableSpaceTracker,
} from "~/components/article/article-render/opinion-article/styles";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { InlineAdSlotRenderProps } from "~/components/content/content-schema-render/inline-ad-slot/hooks";
import type { ExtraProps as LinkExtraProps } from "~/components/content/content-schema-render/link";
import { useContentSchemaRenderLink } from "~/components/content/content-schema-render/link/hooks";
import { MagazinesStyleVariant as variant } from "~/components/section/section-style/const";
import { StoryCardCarouselWidget } from "~/components/section/section-style/section-style-widgets/story-card-carousel-widget";
import type { styleArticleContent$key } from "~/queries/__generated__/styleArticleContent.graphql";

import { ArticleMoreOnThisList } from "./article-more-on-this-list";
import { StyleArticleBlockquoteQuote } from "./blockquote-quote";
import { DigitalArchiveLink } from "./digital-archive-link";
import {
  ActionWidget,
  Author,
  AuthorDetail,
  Content,
  DesktopBeforeContentAdSlot,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Leading,
  MobileUpBeforeContentAdSlot,
  MoreOnThis,
  Section,
  ShareWidget,
  Social,
  StyleArticleContainer,
  SubHeadline,
  Topic,
  TopicSocialContainer,
} from "./styles";

export type Props = {
  reference: styleArticleContent$key;
} & ArticleRenderChildProps;

export const StyleArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment styleArticleContent on Content {
        ...articleImageGalleryContent
        ...styleArticleAuthorBlockArticle
        ...reduceWhitespaceByAvailableSpaceArticle
        ...articleMoreOnThisListStyleArticle
        entityId
      }
    `,
    reference,
  );

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
      widgetsBottom: WidgetsBottomAvailableSpaceTracker,
    },
  });

  const linkExtraProps = useMemo<LinkExtraProps>(
    () => ({
      digitalArchiveLinkComponentRenderFunction: props => <DigitalArchiveLink {...props} />,
    }),
    [],
  );
  const { ComponentWithExtraProps: LinkWithExtraProps } =
    useContentSchemaRenderLink(linkExtraProps);

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(
    () =>
      isDesktopUp
        ? {
            "0": {
              adUnit: "d_banner5",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], [300, 250], [1, 1], "fluid"],
            },
            "1": {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250], [640, 360], [640, 480], [480, 360], [480, 270], [1, 1], "fluid"],
            },
            "2": {
              adUnit: "d_banner2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "3": {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250], "fluid"],
            },
            "4": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "5": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "6": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "7": {
              adUnit: "native4",
              breakpoint: theme.breakpoints.up("desktop"),
            },
          }
        : {
            "0": {
              adUnit: "m_banner5",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [[320, 100], [300, 250], [1, 1], "fluid"],
            },
            "1": {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [320, 180], [1, 1], "fluid"],
            },
            "2": {
              adUnit: "m_lrec1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "3": {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "4": {
              adUnit: "m_lrec4d",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "5": {
              adUnit: "m_lrec4e",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "6": {
              adUnit: "m_lrec4f",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "7": {
              adUnit: "native4",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
          },
    [isDesktopUp, isInlineAdSlotDisabled],
  );

  return (
    <StyleArticleContainer>
      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [1, 1],
            [480, 360],
            [480, 270],
          ],
        })}
      </DesktopHeaderAdSlot>
      <DesktopBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "d_lrec1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[300, 250], "fluid"],
          withLabel: true,
        })}
      </DesktopBeforeContentAdSlot>
      <MobileUpBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [[300, 250], "fluid"],
          withLabel: true,
        })}
      </MobileUpBeforeContentAdSlot>
      {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}
      <Section>
        {provider.sections({
          variant,
        })}
      </Section>
      <Headline>{provider.headline({ variant })}</Headline>
      <Author reference={article} />
      <ArticleImageGallery reference={article}>
        <Leading>
          {provider.leading({
            advertising: {
              instream: "instream3",
            },
            responsiveDescriptionVariants: {
              desktopUp: "show",
              mobileUp: "show",
              tabletUp: "show",
            },
            responsiveImageVariants: {
              desktopUp: "normal",
              mobileUp: "square",
              tabletUp: "normal",
            },
            responsiveVariants: {
              desktopUp: ImageSize["1280x720"],
              mobileUp: ImageSize["768x768"],
              tabletUp: ImageSize["1280x720"],
            },
          })}
        </Leading>
        <Content>
          {provider.body({
            extraComponentMap: {
              a: LinkWithExtraProps,
              "blockquote-quote": StyleArticleBlockquoteQuote,
              "inline-widget": null,
            },
            inlineAdSlots,
            isDisabledArticleImageGallery: true,
          })}
        </Content>
      </ArticleImageGallery>
      <TopicSocialContainer>
        <Follow>{provider.topicFollowWidget({ variant })}</Follow>
        <Social>
          <ShareWidget>{provider.shareWidget({ variant })}</ShareWidget>
          <ActionWidget>{provider.actionWidget({ variant })}</ActionWidget>
        </Social>
      </TopicSocialContainer>
      <SubHeadline>{provider.subHeadline({ variant })}</SubHeadline>
      <Topic>
        {provider.topics({
          label: "TAGS",
          variant,
          withFollow: false,
          withSeparator: false,
        })}
      </Topic>
      <AuthorDetail>{provider.authorDetails({ variant })}</AuthorDetail>
      <MoreOnThis>
        <ArticleMoreOnThisList reference={article} />
      </MoreOnThis>
      <ClientSideSuspense>
        {provider.takeOverAdSlot()}
        <StoryCardCarouselWidget />
      </ClientSideSuspense>
    </StyleArticleContainer>
  );
};

StyleArticle.displayName = "StyleArticle";
